import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Theme, useMediaQuery } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  getReportDownloadLabel,
  reportDateFilterHiddenSx,
  reportDownloadDesktopSx,
} from "screens/Reports/helpers/reportsToolbarStyles";
import ReportsSearchField from "screens/Reports/components/ReportsSearchField";
import ReportsRecordList, {
  ReportRecord,
} from "screens/Reports/components/ReportsRecordList";
import moment from "moment";
import reportsTokens from "screens/Reports/Reports.tokens";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  reportsLoadingRowHeight,
  reportsTableCardSx,
  reportsTableIdentitySx,
  reportsTableNameSx,
  reportsTableStrongValueSx,
  reportsTableSubSx,
  reportsTableSx,
} from "screens/Reports/ReportsTable.styles";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import { handleErrorMessage } from "helpers/methods";
import { DateRange } from "models/interfaces";
import {
  getRecipientsReportCount,
  getRecipientsReportPage,
} from "../services/ReportsService";

type RecipientRow = {
  rowId: string;
  recipient: JSX.Element;
  /** Names this row's selection checkbox for screen readers (WCAG 4.1.2). */
  selectionLabel: string;
  nameText: string;
  emailText: string;
  totalRequests: number;
  signed: number;
  pending: number;
  completionRate: string;
  /**
   * The rate reads a step stronger than the other figures (`rateStyle` in the
   * mock), so the cell carries its own type. `completionRate` stays a plain
   * string alongside it for the CSV export and the compact card list.
   */
  completionRateCell: JSX.Element;
};

const HEADERS = [
  { name: "Recipient", field: "recipient", align: "left", sorting: false },
  {
    name: "Sent",
    field: "totalRequests",
    align: "right",
    sorting: false,
  },
  {
    name: "Pending",
    field: "pending",
    align: "right",
    sorting: false,
  },
  {
    name: "Completed",
    field: "signed",
    align: "right",
    sorting: false,
  },
  {
    name: "Completion Rate",
    field: "completionRateCell",
    align: "right",
    sorting: false,
  },
];

const toNumber = (val: any) => {
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  if (typeof val === "string") {
    const parsed = Number(val.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getAny = (obj: any, keys: string[], fallback: any = "") => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }
  return fallback;
};

const RecipientInsightsReport = ({
  setLoader,
}: {
  setLoader?: (value: boolean) => void;
}) => {
  // Below `md` the reference swaps the table for cards — see
  // `ReportsRecordList`.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });
  const [pickerDateRange, setPickerDateRange] = useState<DateRange>(dateRange);
  const [filters, setFilters] = useState({
    page: 1,
    rowsPerPage: 10,
    searchText: "",
  });

  const [selected, setSelected] = useState<string[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<
    Record<string, RecipientRow>
  >({});

  const convertTableData = (data: any[]): RecipientRow[] => {
    return data.map((item: any, index: number) => {
      const name = getAny(
        item,
        [
          "recipient_name",
          "recipientName",
          "name",
          "full_name",
          "fullName",
          "signer_name",
          "signerName",
        ],
        "-",
      );
      const email = getAny(
        item,
        [
          "recipient_email",
          "recipientEmail",
          "email",
          "signer_email",
          "signerEmail",
        ],
        "-",
      );
      const totalRequests = toNumber(
        getAny(item, [
          "envelopes_sent",
          "envelopesSent",
          "sent",
          "total_envelopes",
          "totalEnvelopes",
          "total_requests",
          "requestCount",
        ]),
      );

      const signed = toNumber(
        getAny(item, [
          "envelopes_completed",
          "envelopesCompleted",
          "completed",
          "signed",
          "signedCount",
          "total_requests",
        ]),
      );
      const pending = toNumber(
        getAny(item, [
          "envelopes_opened",
          "envelopesOpened",
          "pending",
          "opened_count",
          "openedCount",
          "views",
        ]),
      );
      const rateFromApi = getAny(
        item,
        [
          "completion_rate",
          "completionRate",
          "completion_percentage",
          "completionPercentage",
        ],
        null,
      );
      const completionRate =
        rateFromApi !== null && rateFromApi !== undefined && rateFromApi !== ""
          ? typeof rateFromApi === "number"
            ? `${Math.round(rateFromApi)}%`
            : String(rateFromApi).includes("%")
              ? String(rateFromApi)
              : `${String(rateFromApi)}%`
          : totalRequests > 0
            ? `${Math.round((signed / totalRequests) * 100)}%`
            : "0%";

      return {
        rowId: `${String(
          getAny(
            item,
            ["id", "recipient_id", "recipientId", "user_id", "userId", "email"],
            `${email}`,
          ),
        )}-${index}`,
        recipient: (
          <Box sx={reportsTableIdentitySx}>
            <Box sx={reportsTableNameSx} title={String(name)}>
              {String(name)}
            </Box>
            <Box sx={reportsTableSubSx} title={String(email)}>
              {String(email)}
            </Box>
          </Box>
        ),
        selectionLabel: `${String(name)}, ${String(email)}`,
        nameText: String(name),
        emailText: String(email),
        totalRequests,
        signed,
        pending,
        completionRate,
        completionRateCell: (
          <Box component="span" sx={reportsTableStrongValueSx}>
            {completionRate}
          </Box>
        ),
      };
    });
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setLoader?.(true);
      const searchTerms = filters.searchText.trim().split(/\s+/);
      const backendSearchText = searchTerms.length > 0 ? searchTerms[0] : "";
      const payload = {
        dateRange: {
          fromDate: String(dateRange.fromDate || ""),
          toDate: String(dateRange.toDate || ""),
        },
        searchText: backendSearchText,
      };

      const [dataResponse, countResponse] = await Promise.all([
        getRecipientsReportPage(filters.page, payload),
        getRecipientsReportCount(payload),
      ]);

      if (dataResponse) {
        const rowData = getAny(
          dataResponse,
          ["data", "content", "data"],
          Array.isArray(dataResponse) ? dataResponse : [],
        );
        let validRows = Array.isArray(rowData) ? rowData : [];

        if (filters.searchText && searchTerms.length > 1) {
          validRows = validRows.filter((item: any) => {
            const name = String(
              getAny(
                item,
                [
                  "recipient_name",
                  "recipientName",
                  "name",
                  "full_name",
                  "fullName",
                  "signer_name",
                  "signerName",
                ],
                "-",
              ),
            ).toLowerCase();
            const email = String(
              getAny(
                item,
                [
                  "recipient_email",
                  "recipientEmail",
                  "email",
                  "signer_email",
                  "signerEmail",
                ],
                "-",
              ),
            ).toLowerCase();
            return searchTerms.every((term) => {
              const lowerTerm = term.toLowerCase();
              return name.includes(lowerTerm) || email.includes(lowerTerm);
            });
          });
        }

        setRows(convertTableData(validRows));

        let fetchedCount = toNumber(countResponse?.count ?? countResponse ?? 0);
        if (filters.searchText && searchTerms.length > 1) {
          fetchedCount = validRows.length;
        }
        setCount(fetchedCount);
      }
    } catch (error: any) {
      handleErrorMessage(error);
      setRows([]);
      setCount(0);
    } finally {
      setIsLoading(false);
      setLoader?.(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.rowsPerPage, filters.searchText, dateRange]);

  useEffect(() => {
    setSelected([]);
    setSelectedRowsMap({});
  }, [filters.searchText, dateRange]);

  const handlePageChange = (_: any, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setFilters((prev) => ({ ...prev, page: 1, rowsPerPage }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setFilters((prev) => ({ ...prev, page: 1, searchText }));
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    const foundRow = rows.find((r) => r.rowId === id);
    if (foundRow) {
      setSelectedRowsMap((prev) => ({ ...prev, [id]: foundRow }));
    }
  };

  const handleSelectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => {
      const next = [...prev];
      visibleRowIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.rowId] = row;
      });
      return next;
    });
  };

  const handleDeselectOne = (id: string) => {
    setSelected((prev) => prev.filter((item) => item !== id));
    setSelectedRowsMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handleDeselectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      visibleRowIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  };

  const handleDownloadCSV = () => {
    const selectedData = selected
      .map((id) => selectedRowsMap[id])
      .filter(Boolean);

    const headers = [
      "Name",
      "Email",
      "Sent",
      "Pending ",
      "Signed",
      "Completion Rate",
    ];

    const csvRows = selectedData.map((row) => [
      `"${row.nameText.replace(/"/g, '""')}"`,
      `"${row.emailText.replace(/"/g, '""')}"`,
      row.totalRequests,
      row.pending,
      row.signed,
      row.completionRate,
    ]);

    const csvContent = [headers, ...csvRows].map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Recipient_Analytics${moment().format("YYYY-MM-DD")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const records: ReportRecord[] = rows.map((row) => ({
    rowId: row.rowId,
    name: row.nameText,
    sub: row.emailText === "-" ? undefined : row.emailText,
    selectionLabel: row.selectionLabel,
    pairs: [
      { label: "Sent", value: String(row.totalRequests) },
      { label: "Pending", value: String(row.pending) },
      { label: "Completed", value: String(row.signed) },
      { label: "Rate", value: row.completionRate },
    ],
  }));

  return (
    <Stack
      spacing={{ xs: 1.75, sm: 2, md: reportsTokens.toolbar.marginBottom }}
    >
      <Stack
        direction={{ xs: "column", xl: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", xl: "center" }}
      >
        <ReportsSearchField
          placeholder="Search recipients"
          onSearchChange={handleSearchChange}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: reportsTokens.toolbar.gap }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          {/*
            The reference's phone and tablet toolbar is the search field alone —
            the date range stays at its default month there.
          */}
          <Box
            sx={{
              ...reportDateFilterHiddenSx,
              width: { xs: "100%", md: 380 },
              flexShrink: 0,
              alignItems: "stretch",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CustomDateRangePicker
              ariaLabel="Recipient analytics date range"
              isClearable={false}
              openDateRangePicker={openDateRangePicker}
              setOpenDateRangePicker={setOpenDateRangePicker}
              dateRange={pickerDateRange}
              setDateRange={setPickerDateRange}
              helperFunction={(confirmedRange: any) => {
                setDateRange(confirmedRange);
                setFilters((prev) => ({ ...prev, page: 1 }));
              }}
              disableFuture={false}
              hideAllTime
            />
          </Box>

          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadCSV}
            disabled={selected.length === 0}
            sx={reportDownloadDesktopSx}
          >
            {getReportDownloadLabel(selected.length)}
          </Button>
        </Stack>
      </Stack>

      {isCompact ? (
        <ReportsRecordList
          records={records}
          selected={selected}
          onSelectOne={handleSelectOne}
          onDeselectOne={handleDeselectOne}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          isLoading={isLoading}
          onDownload={handleDownloadCSV}
          emptyTitle="No recipients yet"
          emptySub="People you send requests to will appear here with their completion rates."
          ariaLabel="Recipient analytics"
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          count={count}
          onPageChange={(nextPage) => handlePageChange(null, nextPage)}
        />
      ) : (
        <CustomTable
          emptyState={{
            icon: "report",
            title: "No recipient activity yet",
            body: "Once requests are sent and acted on, each recipient's activity is summarised here.",
          }}
          loadingRowHeight={reportsLoadingRowHeight}
          cardSx={reportsTableCardSx}
          tableSxOverride={reportsTableSx}
          ariaLabel="Recipient analytics"
          headers={HEADERS}
          rows={rows}
          count={count}
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          showJumpToPageNumber={false}
          handleOnChangePageInput={handlePageChange}
          showPagination
          isLoading={isLoading}
          showCheckbox={true}
          hideSelectedCount
          highlightSelectedRows
          selected={selected}
          onSelectOne={handleSelectOne}
          onSelectAll={handleSelectAll}
          onDeselectOne={handleDeselectOne}
          onDeselectAll={handleDeselectAll}
        />
      )}
    </Stack>
  );
};

export default RecipientInsightsReport;
